import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import ragService from '../services/rag.service';
import { agentsAPI } from '../services/api';
import {
  getAgents,
  getNotificationContacts,
  getIntegrations,
  sendTestNotification,
} from '../services/tenant.service';
import { useAuthStore } from '../store/authStore';
import { plural } from '../utils/format';

// What to check before customers start chatting. Written for the business
// owner: it used to be our contractor runbook ("Create tenant and users",
// "Customer reviewed and approved"), with checkboxes that had no labels and
// the three steps that matter -- widget installed, test lead, approval --
// linking nowhere.

// Steps only the owner can judge. Each still gets something to click.
const MANUAL_ITEMS = [
  {
    key: 'agent_tested',
    title: 'Your agent answers your common questions',
    description: 'Ask about prices, availability, a callback and something you do not offer, and check each answer.',
    action: { label: 'Open your agent', kind: 'open_agent' },
  },
  {
    key: 'test_lead_sent',
    title: 'A test alert reached your phone or inbox',
    description: 'Send a sample lead to the alert contacts you set up, then tick this once it arrives.',
    action: { label: 'Send test alert', kind: 'test_alert' },
  },
  {
    key: 'support_faq_loaded',
    title: 'After-sale answers added (Growth and Scale)',
    description: 'Add hours, rescheduling, returns and policies so the agent can answer existing customers too.',
    action: { label: 'Open business knowledge', path: '../rag/sources' },
  },
];

const emptyManualState = MANUAL_ITEMS.reduce((acc, item) => ({ ...acc, [item.key]: false }), {});

export default function TenantGoLiveChecklistPage() {
  const { tenantId: urlTenantId } = useParams();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const tenantId = urlTenantId || user?.tenant_id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collections, setCollections] = useState([]);
  const [agents, setAgents] = useState([]);
  const [notificationContacts, setNotificationContacts] = useState(null);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [installHosts, setInstallHosts] = useState(null);
  const [manualState, setManualState] = useState(emptyManualState);
  const [testState, setTestState] = useState({ sending: false, message: '' });

  const storageKey = useMemo(() => `serviti_go_live_checklist_${tenantId || 'unknown'}`, [tenantId]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
      setManualState({ ...emptyManualState, ...saved });
    } catch {
      setManualState(emptyManualState);
    }
  }, [storageKey]);

  useEffect(() => {
    const loadStatus = async () => {
      if (!tenantId) {
        setLoading(false);
        setError('No workspace selected.');
        return;
      }

      try {
        setLoading(true);
        setError('');
        const [collectionsData, agentsData, contactsData, integrationsData, installData] = await Promise.all([
          ragService.getCollections(),
          getAgents(tenantId),
          getNotificationContacts(tenantId),
          getIntegrations(tenantId, 'crm').catch(() => []),
          agentsAPI.getInstallStatus().then((r) => r.data).catch(() => null),
        ]);

        setCollections(collectionsData.collections || []);
        setAgents(agentsData || []);
        setNotificationContacts(contactsData || null);
        setWebhookEnabled((integrationsData || []).some((integration) =>
          ['webhook', 'custom_webhook'].includes(integration.provider) && integration.is_active && integration.base_url
        ));
        setInstallHosts(installData ? (installData.agents || []).flatMap((a) => a.hosts || []) : null);
      } catch (err) {
        console.error('Failed to load launch checklist:', err);
        setError(err?.response?.data?.detail || 'Could not load the checklist. Refresh to try again.');
      } finally {
        setLoading(false);
      }
    };

    loadStatus();
  }, [tenantId]);

  const firstAgent = agents[0];
  const publicAgentUrl = firstAgent?.slug ? `${window.location.origin}/${firstAgent.slug}` : '';
  const totalDocuments = collections.reduce((sum, collection) => sum + (collection.documents_count || 0), 0);
  const totalChunks = collections.reduce((sum, collection) => sum + (collection.chunks_count || 0), 0);
  const notificationEmails = notificationContacts?.notification_emails || [];
  const notificationPhones = notificationContacts?.notification_phones || [];
  const tier = String(notificationContacts?.tier || '').toLowerCase();
  const whatsappIncluded = tier === 'growth' || tier === 'scale';
  const widgetLive = (installHosts || []).length > 0;

  const autoItems = [
    {
      key: 'knowledge_loaded',
      title: 'Business knowledge added',
      description: totalDocuments > 0
        ? `${plural(totalDocuments, 'page or file', 'pages or files')} your agent answers from.`
        : totalChunks > 0
          ? 'Your agent has knowledge to answer from.'
          : 'Add your website or documents so the agent has something to answer from.',
      complete: totalDocuments > 0 || totalChunks > 0,
      action: { label: 'Open business knowledge', path: '../rag/sources' },
    },
    {
      key: 'agent_created',
      title: 'AI agent created',
      description: firstAgent ? `${firstAgent.name} is ready.` : 'Create the agent that answers your visitors.',
      complete: agents.length > 0,
      action: { label: 'Open your agents', path: '../agent-management' },
    },
    {
      key: 'widget_installed',
      title: 'Chat widget on your website',
      description: widgetLive
        ? `Seen on ${installHosts[0].host}.`
        : installHosts === null
          ? 'Could not check right now.'
          : 'Not seen on a website yet. Add one line of code, or send the steps to your developer.',
      complete: widgetLive,
      action: { label: 'Install on your website', path: '../agent-management?install=first' },
    },
    {
      key: 'notifications_configured',
      title: 'Lead alerts set up',
      description: `Email: ${notificationEmails.length ? plural(notificationEmails.length, 'address', 'addresses') : 'your workspace admins'}. WhatsApp: ${
        notificationPhones.length ? plural(notificationPhones.length, 'number') : whatsappIncluded ? 'no number yet' : 'on Growth and Scale plans'
      }.`,
      complete: !(whatsappIncluded && notificationPhones.length === 0),
      action: { label: 'Open lead alerts', path: '../notification-settings' },
    },
    {
      key: 'webhook_configured',
      title: 'Leads sent to your CRM',
      description: webhookEnabled ? 'Each new lead is pushed to your CRM.' : 'Optional on the Scale plan: push each new lead to your CRM.',
      complete: webhookEnabled,
      optional: true,
      action: { label: 'Open lead alerts', path: '../notification-settings' },
    },
  ];

  const allItems = [
    ...autoItems,
    ...MANUAL_ITEMS.map((item) => ({
      ...item,
      complete: Boolean(manualState[item.key]),
      manual: true,
    })),
  ];
  const requiredItems = allItems.filter((item) => !item.optional);
  const completedCount = requiredItems.filter((item) => item.complete).length;
  const completionPercent = Math.round((completedCount / requiredItems.length) * 100);

  const toggleManual = (key) => {
    const next = { ...manualState, [key]: !manualState[key] };
    setManualState(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // Unavailable storage only loses the tick on reload.
    }
  };

  const runAction = async (action) => {
    if (action.path) {
      navigate(action.path);
      return;
    }
    if (action.kind === 'open_agent' && publicAgentUrl) {
      window.open(publicAgentUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (action.kind === 'test_alert') {
      setTestState({ sending: true, message: '' });
      try {
        const result = await sendTestNotification(tenantId);
        const parts = [];
        if (result.email?.sent) parts.push(`email to ${plural(result.email.recipients, 'address', 'addresses')}`);
        if (result.whatsapp?.sent) parts.push(`WhatsApp to ${plural(result.whatsapp.recipients, 'number')}`);
        setTestState({
          sending: false,
          message: parts.length ? `Sent ${parts.join(' and ')}. Tick this once it arrives.` : 'Nothing was sent. Check your lead alert contacts.',
        });
      } catch (err) {
        setTestState({ sending: false, message: err?.response?.data?.detail || 'Could not send a test alert.' });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <ClipboardDocumentCheckIcon className="h-9 w-9 text-indigo-600 mt-1" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Launch checklist</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Everything to check before customers start chatting with your agent.
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{completionPercent}%</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{completedCount} of {requiredItems.length} done</div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {allItems.map((item) => {
          const inputId = `launch-${item.key}`;
          const isTest = item.action?.kind === 'test_alert';
          return (
            <div
              key={item.key}
              className={`rounded-lg border p-4 bg-white shadow-sm dark:bg-gray-800 ${
                item.complete || item.optional ? 'border-gray-200 dark:border-gray-700' : 'border-amber-200 dark:border-amber-700'
              }`}
            >
              <div className="flex items-start gap-3">
                {item.manual ? (
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={item.complete}
                    onChange={() => toggleManual(item.key)}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                ) : item.complete || item.optional ? (
                  <CheckCircleIcon aria-hidden="true" className={`mt-0.5 h-6 w-6 ${item.complete ? 'text-green-600' : 'text-gray-300'}`} />
                ) : (
                  <ExclamationTriangleIcon aria-hidden="true" className="mt-0.5 h-6 w-6 text-amber-500" />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {item.manual ? (
                      <label htmlFor={inputId} className="font-semibold text-gray-900 dark:text-white">{item.title}</label>
                    ) : (
                      <h2 className="font-semibold text-gray-900 dark:text-white">
                        {item.title}
                        <span className="sr-only">{item.complete ? ' (done)' : ' (to do)'}</span>
                      </h2>
                    )}
                    {item.optional && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">Optional</span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
                  {item.action && !(item.action.kind === 'open_agent' && !publicAgentUrl) && (
                    <button
                      type="button"
                      onClick={() => runAction(item.action)}
                      disabled={isTest && testState.sending}
                      className="mt-3 inline-flex min-h-[44px] items-center text-sm font-semibold text-indigo-700 hover:text-indigo-900 disabled:opacity-50 dark:text-indigo-300"
                    >
                      {isTest && testState.sending ? 'Sending…' : item.action.label}
                    </button>
                  )}
                  {isTest && testState.message && (
                    <p role="status" className="mt-1 text-sm text-gray-700 dark:text-gray-200">{testState.message}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
